import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface PrimaryButtonProps {
    children: ReactNode
    href?: string
    type?: 'button' | 'submit'
    disabled?: boolean
    busy?: boolean
    className?: string
    intro?: boolean
    onClick?: () => void
}

export const PrimaryButton: FC<PrimaryButtonProps> = ({
    children,
    href,
    type = 'button',
    disabled = false,
    busy = false,
    className,
    intro = false,
    onClick
}): ReactNode => {
    const classes = cn(
        'group inline-flex h-12 shrink-0 select-none items-stretch',
        (disabled || busy) && 'cursor-not-allowed',
        className
    )

    const inner = (
        <>
            <span
                className={cn(
                    'hud flex items-center bg-ink px-5 text-background transition-colors duration-300 ease-instrument',
                    intro && 'intro-wipe',
                    !disabled && !busy && 'group-hover:bg-[#FFFDF6]',
                    (disabled || busy) && 'bg-ink/70'
                )}
            >
                {children}
            </span>

            <span className={cn('flex w-12 items-center justify-center bg-signal text-background', intro && 'intro-cell')} aria-hidden='true'>
                <ArrowRight
                    className={cn(
                        'size-4 transition-transform duration-500 ease-instrument',
                        busy ? 'animate-pulse' : 'group-hover:translate-x-0.5'
                    )}
                    strokeWidth={1.75}
                />
            </span>
        </>
    )

    if (href) {
        return (
            <Link href={href} className={classes}>
                {inner}
            </Link>
        )
    }

    return (
        <button
            type={type}
            disabled={disabled || busy}
            aria-busy={busy || undefined}
            onClick={onClick}
            className={classes}
        >
            {inner}
        </button>
    )
}
